import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { CONTACT_LENS_MODALITIES, RECALL_KINDS, RECALL_RULE_FIELDS } from '@lensmira/domain'
import type { RecallKind } from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { recallKindLabel } from '../status-labels.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * The rules that decide who gets brought back. §6.14 `APT-004` (the four rules and their
 * intervals), `APT-006` (the replenishment cadence and cap); `decisions/0004`; `OPEN.md` §4.4.
 *
 * **How this differs from `Recalls.tsx`.** That screen ACTS on recalls: it lists the queue a
 * campaign run produced and records the call, the send and the outcome, and it is open to anyone
 * holding `recall_run_campaign` (owner, manager, dispenser). This one SETS the policy that run
 * reads, which is `tenant_settings_policies` (the owner alone), because a rule decides who is
 * contacted and how often across every customer of the shop. A dispenser who can run a campaign
 * must not be able to change the interval it runs on. Before this screen no `recall_rule` row
 * could be created from the counter, so a run answered "No active rule" for ever.
 *
 * **Nothing here has a default.** `APT-004` says "N months" and names no N, for any field, and
 * `decisions/0004` confirms the spec supplies none. Every number starts empty and the copy asks
 * rather than suggests; a rule cannot be switched on until its numbers are filled in.
 *
 * **Warranty expiry is shown, disabled, with the reason.** A rule for it can be stored and switched
 * on and every campaign run skips it, because nothing records when a warranty ends. Hiding the row
 * would leave a shopkeeper wondering where it went and offering the switch would be a switch that
 * does nothing, so the row is there, says why it is dead, and has nothing to press.
 *
 * **The run trigger is not here.** `POST /recalls/runs` is `recall_run_campaign`, a wider row than
 * this screen's. A manager who can run the campaign cannot see this screen at all, and the result
 * of a run (who is due, who is withheld, which kind was skipped) belongs next to the queue it
 * filled. So the trigger goes on `Recalls.tsx`, and this screen links there when it is given
 * `onOpenRecalls`.
 *
 * **Two server rules shape every body this sends:**
 *
 * - `parseRecallRule` runs before the omitted-field merge, so `repeatEveryDays` and `maxNudges`
 *   must travel together or the save is refused `RECALL_NUDGE_CAP_REQUIRED` even when the stored
 *   row has both. A contact lens save always sends both, as numbers or as two nulls.
 * - `daysPerUnit: {}` on any kind but contact lens is `RECALL_FIELD_NOT_FOR_KIND`, because an
 *   empty object is not null. A field the kind does not read is never put in the body.
 *
 * A refusal from a queued write halts the queue, taking payment included, so everything the server
 * would refuse is refused here first, in the shopkeeper's words.
 */

/** One kind's rule as the counter reads it. `GET /recall-rules` returns `{ rules: [...] }`. */
export interface RecallRuleData {
  readonly kind: string
  readonly configured: boolean
  readonly isActive: boolean
  readonly intervalMonths: number | null
  readonly leadDays: number | null
  readonly reorderLeadDays: number | null
  readonly daysPerUnit: Readonly<Partial<Record<string, number>>> | null
  readonly repeatEveryDays: number | null
  readonly maxNudges: number | null
  /** Null until the rule exists. */
  readonly version: number | null
}

export interface RecallRulesProps {
  /** `tenant_settings_policies`: the owner alone. Without it nothing is read and nothing shown. */
  readonly canManage: boolean
  readonly loadRules: () => Promise<readonly RecallRuleData[]>
  /** Where a campaign is run and its queue worked. Shown as a button when given. */
  readonly onOpenRecalls?: () => void
  readonly onWritten?: () => void
}

/** The ceilings `RECALL_LIMITS` sets on the server: 100 years of months, 10 years of days. */
const LIMITS = { intervalMonths: 1200, days: 3650, maxNudges: 100 } as const

type Modality = (typeof CONTACT_LENS_MODALITIES)[number]

interface Draft {
  readonly isActive: boolean
  readonly intervalMonths: string
  readonly reorderLeadDays: string
  readonly days: Readonly<Record<Modality, string>>
  readonly repeatEveryDays: string
  readonly maxNudges: string
}

type NumberKey = 'intervalMonths' | 'reorderLeadDays' | 'repeatEveryDays' | 'maxNudges'

const MODALITY_KEY = {
  daily: 'clRx.modality.daily',
  two_weekly: 'clRx.modality.twoWeekly',
  monthly: 'clRx.modality.monthly',
  quarterly: 'clRx.modality.quarterly',
  annual: 'clRx.modality.annual',
} as const

const EXPLAIN_KEY = {
  sight_test: 'recallRules.sightTest.explain',
  contact_lens: 'recallRules.contactLens.explain',
  frame_refresh: 'recallRules.frameRefresh.explain',
  warranty_expiry: 'recallRules.warrantyExpiry.explain',
} as const

const text = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value)

const BLANK = (value: string): boolean => value.trim() === ''

const draftOf = (row: RecallRuleData | undefined): Draft => ({
  isActive: row?.isActive ?? false,
  intervalMonths: text(row?.intervalMonths),
  reorderLeadDays: text(row?.reorderLeadDays),
  days: Object.fromEntries(
    CONTACT_LENS_MODALITIES.map((modality) => [modality, text(row?.daysPerUnit?.[modality])]),
  ) as Record<Modality, string>,
  repeatEveryDays: text(row?.repeatEveryDays),
  maxNudges: text(row?.maxNudges),
})

const draftsOf = (rows: readonly RecallRuleData[]): Record<RecallKind, Draft> =>
  Object.fromEntries(
    RECALL_KINDS.map((kind) => [kind, draftOf(rows.find((row) => row.kind === kind))]),
  ) as Record<RecallKind, Draft>

export function RecallRules({
  canManage,
  loadRules,
  onOpenRecalls,
  onWritten,
}: RecallRulesProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [rows, setRows] = useState<readonly RecallRuleData[] | null>(null)
  /** A failed read, kept apart from `errors` so a bad number cannot turn the list into a failure. */
  const [loadError, setLoadError] = useState(false)
  const [form, setForm] = useState<Record<RecallKind, Draft>>(() => draftsOf([]))
  const [errors, setErrors] = useState<Partial<Record<RecallKind, string>>>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(() => {
    loadRules().then(
      (loaded) => {
        setRows(loaded)
        setForm(draftsOf(loaded))
        setLoadError(false)
      },
      () => {
        // Its own state: "the settings could not be read" is not "nothing is set".
        setRows(null)
        setLoadError(true)
      },
    )
  }, [loadRules])

  useEffect(() => {
    // A role without the row is not asked: the read would answer 403 for nothing.
    if (canManage) refresh()
  }, [canManage, refresh])

  useWriteSettled(snapshot, writeId, (write) => {
    const outcome = (write.response as { outcome?: unknown } | undefined)?.outcome
    setNotice(t(outcome === 'unchanged' ? 'recallRules.savedUnchanged' : 'recallRules.saved'))
    setWriteId(null)
    refresh()
    onWritten?.()
  })

  // A write somebody discarded in `BlockedWrites` never reaches `sent`, so without this the Save
  // buttons would stay disabled for good.
  useEffect(() => {
    if (writeId === null) return
    if (!snapshot.writes.some((candidate) => candidate.id === writeId)) setWriteId(null)
  }, [snapshot, writeId])

  const edit = (kind: RecallKind, change: Partial<Draft>): void => {
    setForm((was) => ({ ...was, [kind]: { ...was[kind], ...change } }))
    setErrors((was) => ({ ...was, [kind]: undefined }))
    setNotice(null)
  }

  const save = async (kind: RecallKind, row: RecallRuleData | undefined): Promise<void> => {
    const draft = form[kind]
    const fail = (message: string): void => {
      setErrors((was) => ({ ...was, [kind]: message }))
    }
    const reads = RECALL_RULE_FIELDS[kind]

    // Whole numbers inside the server's ceilings, field by field, so the refusal names the field.
    const checks: [string, number, number, string][] = []
    if (reads.includes('intervalMonths')) {
      checks.push([draft.intervalMonths, 1, LIMITS.intervalMonths, t('recallRules.intervalMonths')])
    }
    if (kind === 'contact_lens') {
      checks.push([draft.reorderLeadDays, 0, LIMITS.days, t('recallRules.reorderLeadDays')])
      for (const modality of CONTACT_LENS_MODALITIES) {
        checks.push([
          draft.days[modality],
          1,
          LIMITS.days,
          `${t('recallRules.daysPerUnit')}: ${t(MODALITY_KEY[modality])}`,
        ])
      }
      checks.push([draft.repeatEveryDays, 1, LIMITS.days, t('recallRules.repeatEveryDays')])
      checks.push([draft.maxNudges, 1, LIMITS.maxNudges, t('recallRules.maxNudges')])
    }
    for (const [value, min, max, name] of checks) {
      const trimmed = value.trim()
      if (trimmed === '') continue
      if (!/^\d+$/.test(trimmed) || Number(trimmed) < min || Number(trimmed) > max) {
        fail(t('recallRules.notANumber', { field: name, min, max }))
        return
      }
    }

    // `RECALL_NUDGE_CAP_REQUIRED`: how often and how many, or neither.
    if (kind === 'contact_lens' && BLANK(draft.repeatEveryDays) !== BLANK(draft.maxNudges)) {
      fail(t('recallRules.repeatHint'))
      return
    }

    const filled = CONTACT_LENS_MODALITIES.filter((m) => !BLANK(draft.days[m]))
    if (draft.isActive) {
      // `RECALL_RULE_INCOMPLETE` and `RECALL_DAYS_PER_UNIT_EMPTY`: a rule that is on and contacts
      // nobody is the opposite of what the switch says.
      const missing: string[] = []
      if (reads.includes('intervalMonths') && BLANK(draft.intervalMonths)) {
        missing.push(t('recallRules.intervalMonths'))
      }
      if (reads.includes('daysPerUnit') && filled.length === 0) {
        missing.push(t('recallRules.daysPerUnit'))
      }
      if (reads.includes('reorderLeadDays') && BLANK(draft.reorderLeadDays)) {
        missing.push(t('recallRules.reorderLeadDays'))
      }
      if (missing.length > 0) {
        fail(t('recallRules.incomplete', { fields: missing.join(', ') }))
        return
      }
    }

    const whole = (value: string): number | null => (BLANK(value) ? null : Number(value.trim()))
    const body: Record<string, unknown> = { isActive: draft.isActive }
    if (reads.includes('intervalMonths')) body['intervalMonths'] = whole(draft.intervalMonths)
    if (kind === 'contact_lens') {
      body['reorderLeadDays'] = whole(draft.reorderLeadDays)
      // Null, never `{}`, when no modality is filled. Only reachable while the rule is off.
      body['daysPerUnit'] =
        filled.length === 0
          ? null
          : Object.fromEntries(filled.map((m) => [m, Number(draft.days[m].trim())]))
      // Both, always: the server checks the pair before it merges anything stored.
      body['repeatEveryDays'] = whole(draft.repeatEveryDays)
      body['maxNudges'] = whole(draft.maxNudges)
    }
    if (row?.version !== null && row?.version !== undefined) body['expectedVersion'] = row.version

    setErrors((was) => ({ ...was, [kind]: undefined }))
    setNotice(null)
    const write = (await enqueue({
      method: 'PUT',
      path: `/recall-rules/${encodeURIComponent(kind)}`,
      label: t('recallRules.queueLabel'),
      body,
    })) as { id: string }
    setWriteId(write.id)
  }

  const numberInput = (
    kind: RecallKind,
    key: NumberKey,
    label: string,
    hint?: string,
  ): JSX.Element => {
    const id = `recall-rule-${kind}-${key}`
    return (
      <div className="lm-field">
        <label className="lm-field__label" htmlFor={id}>
          {label}
        </label>
        <input
          id={id}
          className="lm-input"
          type="text"
          inputMode="numeric"
          dir="ltr"
          autoComplete="off"
          {...(hint === undefined ? {} : { 'aria-describedby': `${id}-hint` })}
          value={form[kind][key]}
          onChange={(e) => {
            edit(kind, { [key]: e.currentTarget.value })
          }}
        />
        {hint === undefined ? null : (
          <p id={`${id}-hint`} className="lm-hint">
            {hint}
          </p>
        )}
      </div>
    )
  }

  const card = (kind: RecallKind): JSX.Element => {
    const row = rows?.find((candidate) => candidate.kind === kind)
    const draft = form[kind]
    const dead = kind === 'warranty_expiry'
    const error = errors[kind]
    const blankModalities = CONTACT_LENS_MODALITIES.filter((m) => BLANK(draft.days[m]))
    return (
      <li key={kind} className="lm-supplier">
        <span className="lm-supplier__name">{recallKindLabel(t, kind)}</span>
        <span className="lm-supplier__meta">
          {row === undefined || !row.configured
            ? t('recallRules.statusNotSet')
            : t(row.isActive ? 'recallRules.statusOn' : 'recallRules.statusOff')}
        </span>
        <fieldset className="lm-fieldset lm-supplier__form" disabled={dead}>
          <legend className="lm-visually-hidden">{recallKindLabel(t, kind)}</legend>
          <p className="lm-hint">{t(EXPLAIN_KEY[kind])}</p>

          {kind === 'sight_test' || kind === 'frame_refresh'
            ? numberInput(
                kind,
                'intervalMonths',
                t('recallRules.intervalMonths'),
                t('recallRules.intervalMonthsHint'),
              )
            : null}

          {kind === 'contact_lens' ? (
            <>
              {numberInput(kind, 'reorderLeadDays', t('recallRules.reorderLeadDays'))}
              <fieldset className="lm-fieldset">
                <legend className="lm-field__label">{t('recallRules.daysPerUnit')}</legend>
                {CONTACT_LENS_MODALITIES.map((modality) => {
                  const id = `recall-rule-${kind}-days-${modality}`
                  return (
                    <div key={modality} className="lm-field">
                      <label className="lm-field__label" htmlFor={id}>
                        {t(MODALITY_KEY[modality])}
                      </label>
                      <input
                        id={id}
                        className="lm-input"
                        type="text"
                        inputMode="numeric"
                        dir="ltr"
                        autoComplete="off"
                        value={draft.days[modality]}
                        onChange={(e) => {
                          const next = e.currentTarget.value
                          edit(kind, { days: { ...draft.days, [modality]: next } })
                        }}
                      />
                    </div>
                  )
                })}
                <p className="lm-hint">{t('recallRules.daysPerUnitHint')}</p>
                {draft.isActive &&
                blankModalities.length > 0 &&
                blankModalities.length < CONTACT_LENS_MODALITIES.length ? (
                  <p className="lm-hint">
                    {t('recallRules.modalityBlank', {
                      modalities: blankModalities.map((m) => t(MODALITY_KEY[m])).join(', '),
                    })}
                  </p>
                ) : null}
              </fieldset>
              <fieldset className="lm-fieldset">
                <legend className="lm-field__label">{t('recallRules.repeat')}</legend>
                {numberInput(kind, 'repeatEveryDays', t('recallRules.repeatEveryDays'))}
                {numberInput(kind, 'maxNudges', t('recallRules.maxNudges'))}
                <p className="lm-hint">{t('recallRules.repeatHint')}</p>
              </fieldset>
            </>
          ) : null}

          {dead ? null : (
            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('recallRules.isActive')}</span>
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => {
                  edit(kind, { isActive: e.currentTarget.checked })
                }}
              />
            </label>
          )}

          {error === undefined ? null : (
            <p className="lm-supplier__error" role="alert">
              {error}
            </p>
          )}

          {dead ? null : (
            <button
              type="button"
              className="lm-button lm-button--primary"
              disabled={writeId !== null}
              onClick={() => {
                void save(kind, row)
              }}
            >
              {t('recallRules.save')}
            </button>
          )}
        </fieldset>
      </li>
    )
  }

  const line = statusLine(t, snapshot, writeId, t('queue.subject.recallRule'))
  const inFlight = writeId === null ? undefined : snapshot.writes.find((w) => w.id === writeId)
  const conflicted = inFlight?.status === 'blocked' && inFlight.error?.code === 'VERSION_CONFLICT'

  return (
    <section className="lm-screen" aria-labelledby="recall-rules-heading">
      <h1 id="recall-rules-heading" className="lm-screen__heading">
        {t('recallRules.heading')}
      </h1>

      <DataSurface
        state={
          !canManage
            ? { kind: 'denied' }
            : loadError
              ? { kind: 'error', message: t('recallRules.loadFailed'), onRetry: refresh }
              : rows === null
                ? { kind: 'loading' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={4}
        what={t('recallRules.what')}
        deniedMessage={t('recallRules.denied')}
      >
        <p className="lm-hint">{t('recallRules.means')}</p>
        <ul className="lm-supplier-list">{RECALL_KINDS.map(card)}</ul>

        <p className="lm-hint">{t('recallRules.runHint')}</p>
        {onOpenRecalls === undefined ? null : (
          <button type="button" className="lm-button lm-button--secondary" onClick={onOpenRecalls}>
            {t('recallRules.openRecalls')}
          </button>
        )}

        {notice === null ? null : (
          <p className="lm-hint" role="status">
            {notice}
          </p>
        )}
        {conflicted ? (
          <p className="lm-hint" role="alert">
            {t('recallRules.changedElsewhere')}
          </p>
        ) : null}
        <p className="lm-hint">{line}</p>
      </DataSurface>
    </section>
  )
}
